import baseStyled, { css } from 'styled-components';
import type { ThemedStyledInterface } from 'styled-components';

import {
  AMARELO,
  ESPACAMENTO,
  FAMILIA_DE_MARCA,
  GRADIENTE_DE_MARCA,
  MOVIMENTO,
  NEUTROS,
  RAIOS,
  ROXO,
  SOMBRAS,
  SOMBRAS_ESCURAS,
  TIPOGRAFIA,
  TRACKING,
} from './tokensDaMarca';

interface DsColors {
  page: string;
  surface: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  grid: string;
  baseline: string;
  border: string;
  /**
   * A barra de rolagem: o trilho por onde o polegar corre e o polegar em
   * repouso. O trilho é translúcido de propósito — a mesma barra aparece sobre
   * página, cartão, gaveta e modal, e cada um tem um fundo diferente.
   *
   * O polegar é opaco pelo motivo oposto: ele é o controle, precisa dos 3:1 de
   * elemento não textual, e translucidez sobre fundo desconhecido não permite
   * garantir isso. É o neutro médio que o tema já usa onde não há texto.
   */
  scrollTrilha: string;
  scrollPolegar: string;
  /**
   * O acento da interface: botão primário, item ativo, foco, seleção. É o roxo
   * da marca, que o guia manda dominar — no tema escuro, o tom 200 da mesma
   * rampa, porque o 500 sobre fundo ink fica em 1,3:1 e desaparece.
   *
   * Serve de preenchimento e de texto porque, dentro do tema certo, os dois
   * usos passam com folga.
   */
  accent: string;
  accentSoft: string;
  /** Texto sobre o acento preenchido: branco sobre o roxo 500, ink sobre o 200. */
  onAccent: string;
  /**
   * O amarelo da Inside e a tinta escura em que ele é aplicado. São os dois
   * únicos valores iguais nos dois temas: marca não muda de cor com a
   * preferência de quem olha. Como o amarelo só tem contraste sobre escuro
   * (1,9:1 sobre a superfície clara, 8,2:1 sobre esta tinta), toda superfície
   * que exibe a marca usa `marcaFundo` — é o fundo que se adapta, não ela.
   */
  marca: string;
  marcaFundo: string;
  statusGood: string;
  /**
   * O amarelo escurecido da própria marca. Antes era laranja, para não se
   * confundir com o acento — agora que o acento é o roxo, "atenção" pode usar
   * a cor que o guia reserva para chamar atenção.
   */
  statusWarning: string;
  /**
   * O guia proíbe vermelho em peças de marca. Aqui ele sobrevive num único
   * papel: sinal de sistema — erro, ação destrutiva e variação negativa. Nunca
   * decorativo, nunca em gráfico, nunca como fundo de área. Trocá-lo por roxo
   * faria "erro" e "botão primário" terem a mesma cor.
   */
  statusCritical: string;
  deltaUp: string;
  deltaDown: string;
}

export interface DsTheme {
  name: 'light' | 'dark';
  colors: DsColors;
  /** O único gradiente permitido: o gradiente de marca do guia. */
  gradiente: string;
  series: string[];
  categorical: string[];
  restante: string;
  funnel: string[];
  /**
   * As paletas que o autor do dashboard pode escolher para as cores de dados de
   * um widget. Nomeadas, e não hex fixos, justamente para trocarem de tom junto
   * com o tema — o mesmo motivo de `series` e `categorical` morarem aqui.
   */
  paletas: Record<string, string[]>;
  font: {
    family: string;
    mono: string;
    /** micro, 12 px */
    sizeXs: string;
    /** apoio, 14 px */
    sizeSm: string;
    /** corpo, 16 px — o piso que o guia exige para texto */
    sizeMd: string;
    /** H4, 22 px */
    sizeLg: string;
    /** H3, 28 px */
    sizeXl: string;
    /** H2, 36 px */
    sizeDisplay: string;
    /** H1, 48 px */
    sizeH1: string;
    /** 88 px, a chamada de uma peça inteira */
    sizeHero: string;
    /**
     * 13 px, a exceção do guia para número em grade densa: célula de tabela,
     * célula de pivot, rótulo de eixo. Texto corrido nunca usa este degrau.
     */
    sizeDado: string;
  };
  /** A entrelinha de cada degrau da escala, na mesma ordem da tabela do guia. */
  leading: {
    hero: number;
    h1: number;
    h2: number;
    h3: number;
    h4: number;
    corpo: number;
    apoio: number;
    micro: number;
    dado: number;
  };
  tracking: {
    titulo: string;
    eyebrow: string;
  };
  weight: {
    normal: number;
    medium: number;
    semibold: number;
    bold: number;
  };
  space: {
    xs: string;
    sm: string;
    md: string;
    lg: string;
    xl: string;
    xxl: string;
    xxxl: string;
    huge: string;
    giant: string;
  };
  radius: {
    sm: string;
    md: string;
    lg: string;
    xl: string;
    xxl: string;
    pill: string;
  };
  shadow: {
    sm: string;
    md: string;
    lg: string;
    /** A sombra roxa da marca, para o que precisa flutuar como peça. */
    brand: string;
    /** O mesmo que `md`: é o nome que os cartões já usavam. */
    card: string;
  };
  motion: {
    rapido: string;
    base: string;
    lento: string;
    easing: string;
  };
}

const font = {
  family: FAMILIA_DE_MARCA,
  mono: `ui-monospace, 'Cascadia Code', Consolas, monospace`,
  sizeXs: TIPOGRAFIA.micro.tamanho,
  sizeSm: TIPOGRAFIA.apoio.tamanho,
  sizeMd: TIPOGRAFIA.corpo.tamanho,
  sizeLg: TIPOGRAFIA.h4.tamanho,
  sizeXl: TIPOGRAFIA.h3.tamanho,
  sizeDisplay: TIPOGRAFIA.h2.tamanho,
  sizeH1: TIPOGRAFIA.h1.tamanho,
  sizeHero: TIPOGRAFIA.hero.tamanho,
  sizeDado: TIPOGRAFIA.dado.tamanho,
};

const leading = {
  hero: TIPOGRAFIA.hero.entrelinha,
  h1: TIPOGRAFIA.h1.entrelinha,
  h2: TIPOGRAFIA.h2.entrelinha,
  h3: TIPOGRAFIA.h3.entrelinha,
  h4: TIPOGRAFIA.h4.entrelinha,
  corpo: TIPOGRAFIA.corpo.entrelinha,
  apoio: TIPOGRAFIA.apoio.entrelinha,
  micro: TIPOGRAFIA.micro.entrelinha,
  dado: TIPOGRAFIA.dado.entrelinha,
};

/**
 * Duas famílias de peso, como o guia manda: 400 e 700. `medium` e `semibold`
 * continuam existindo porque metade da interface já os chama — eles apenas
 * apontam para os dois pesos que a marca tem.
 */
const weight = {
  normal: 400,
  medium: 400,
  semibold: 700,
  bold: 700,
};

/** Grade base de 4 px: toda distância da interface é um destes valores. */
const space = {
  xs: ESPACAMENTO[0],
  sm: ESPACAMENTO[1],
  md: ESPACAMENTO[2],
  lg: ESPACAMENTO[3],
  xl: ESPACAMENTO[4],
  xxl: ESPACAMENTO[5],
  xxxl: ESPACAMENTO[6],
  huge: ESPACAMENTO[7],
  giant: ESPACAMENTO[8],
};

/** Geometria arredondada e generosa; botão em cápsula usa `pill`. */
const radius = {
  sm: RAIOS[0],
  md: RAIOS[1],
  lg: RAIOS[2],
  xl: RAIOS[3],
  xxl: RAIOS[4],
  pill: '999px',
};

const tracking = { titulo: TRACKING.titulo, eyebrow: TRACKING.eyebrow };

/**
 * Rótulo e ordem das paletas na tela, uma vez só: o texto não depende do tema, e
 * repeti-lo no claro e no escuro é convite para os dois divergirem.
 *
 * "Padrão" não está aqui de propósito — é a ausência de escolha, e cai nos
 * tokens do tema (`series`, `categorical`, `funnel`, `accent`).
 */
export const PALETAS_WIDGET: Array<{ id: string; rotulo: string }> = [
  { id: 'marca', rotulo: 'Marca' },
  { id: 'roxo', rotulo: 'Roxo' },
  { id: 'amarelo', rotulo: 'Amarelo' },
  { id: 'verde', rotulo: 'Verde' },
  { id: 'magenta', rotulo: 'Magenta' },
  { id: 'ink', rotulo: 'Ink' },
];

/**
 * Cada tom passa de 3:1 contra `page` e `surface` do seu tema — o mínimo de
 * contraste de elemento não textual, verificado em `theme.spec.ts`. É o que
 * limita a rampa clara a tons médios e escuros, e a escura ao contrário.
 *
 * Nenhuma paleta tem vermelho nem azul: o guia diz que os dois não pertencem à
 * marca, e o vermelho que resta na interface é sinal de erro, não cor de dado.
 */
const paletasClaras: Record<string, string[]> = {
  marca: ['#5B2A8C', '#B87E00', '#855BB2', '#8A5E00', '#3D1C60', '#664400'],
  roxo: ['#855BB2', '#7C46AD', '#5B2A8C', '#4A2172', '#3D1C60', '#2B1D40'],
  amarelo: ['#B87E00', '#A06D00', '#8A5E00', '#7A5200', '#664400', '#523700'],
  verde: ['#17936A', '#0F8A62', '#0F7A54', '#0B6344', '#084C34', '#063826'],
  magenta: ['#C13C8A', '#A3266E', '#8C1F5E', '#75194E', '#5E143E', '#47102F'],
  ink: ['#757184', '#666076', '#574F69', '#493F5B', '#3A2E4E', '#2B1D40'],
};

const paletasEscuras: Record<string, string[]> = {
  marca: ['#CBB2E2', '#F9AE00', '#A47FC7', '#FCC73F', '#855BB2', '#D89700'],
  roxo: ['#E3D4F2', '#CBB2E2', '#B99AD6', '#A47FC7', '#9268B8', '#855BB2'],
  amarelo: ['#FFF0BF', '#FFDD85', '#FCC73F', '#F9AE00', '#D89700', '#B87E00'],
  verde: ['#7FD9B8', '#57C99F', '#31B686', '#199E70', '#178A63', '#12855D'],
  magenta: ['#F9D2E6', '#F5B8D6', '#EE93BE', '#E56FA8', '#D64C8F', '#C93F86'],
  ink: ['#E2E1E7', '#CCCBD3', '#B6B4BF', '#A19EAC', '#8B8798', '#757184'],
};

export const dsLight: DsTheme = {
  name: 'light',
  colors: {
    page: NEUTROS.gray50,
    surface: NEUTROS.branco,
    textPrimary: NEUTROS.ink800,
    textSecondary: NEUTROS.gray700,
    // O `gray500` do guia (#757184) rende 4,40:1 sobre a página — abaixo do
    // mínimo de 4,5:1 que o próprio guia exige para texto. Este é o mesmo tom
    // escurecido o suficiente para passar; o `gray500` puro segue em uso onde
    // não carrega texto (eixo de gráfico, "Outros").
    textMuted: '#6C687B',
    grid: NEUTROS.gray200,
    // Tingido de roxo, e não de cinza: é a linha de base do gráfico, e o guia
    // manda a marca dominar até nos detalhes.
    baseline: ROXO[200],
    border: 'rgba(43,29,64,0.12)',
    scrollTrilha: 'rgba(43,29,64,0.08)',
    scrollPolegar: NEUTROS.gray500,
    accent: ROXO[500],
    accentSoft: ROXO[50],
    onAccent: NEUTROS.branco,
    marca: AMARELO[400],
    marcaFundo: NEUTROS.ink800,
    statusGood: '#0F7A54',
    statusWarning: '#A06D00',
    statusCritical: '#D03B3B',
    deltaUp: '#0F7A54',
    deltaDown: '#D03B3B',
  },
  gradiente: GRADIENTE_DE_MARCA,
  series: [ROXO[500], AMARELO[600]],
  categorical: [ROXO[500], AMARELO[600], '#0F7A54', '#A3266E', ROXO[400], NEUTROS.gray700],
  restante: NEUTROS.gray500,
  funnel: [ROXO[200], ROXO[400], ROXO[500], ROXO[700]],
  paletas: paletasClaras,
  font,
  leading,
  tracking,
  weight,
  space,
  radius,
  shadow: { ...SOMBRAS, card: SOMBRAS.md },
  motion: MOVIMENTO,
};

/**
 * O guia não define tema escuro. Estes tons são derivados do ink da marca: a
 * página é um passo abaixo dele, a superfície é ele mesmo, e o que precisa de
 * contraste sobe pela rampa do roxo em vez de virar cinza.
 */
export const dsDark: DsTheme = {
  name: 'dark',
  colors: {
    page: '#221631',
    surface: NEUTROS.ink800,
    textPrimary: NEUTROS.branco,
    textSecondary: NEUTROS.gray200,
    textMuted: '#A79FB8',
    grid: '#3B2B52',
    baseline: '#4C3A66',
    border: 'rgba(255,255,255,0.12)',
    scrollTrilha: 'rgba(255,255,255,0.08)',
    // O `gray500` do guia não chega a 3:1 sobre a página escura; este é o mesmo
    // neutro clareado o quanto precisa — o tom que o tema escuro já usa em
    // `restante`, para a paleta não ganhar mais um cinza.
    scrollPolegar: '#8B86A0',
    accent: ROXO[200],
    accentSoft: ROXO[700],
    onAccent: NEUTROS.ink800,
    marca: AMARELO[400],
    marcaFundo: NEUTROS.ink800,
    statusGood: '#31B686',
    statusWarning: AMARELO[400],
    statusCritical: '#E66767',
    deltaUp: '#31B686',
    deltaDown: '#E66767',
  },
  gradiente: GRADIENTE_DE_MARCA,
  series: [ROXO[200], AMARELO[400]],
  categorical: ['#B99AD6', AMARELO[400], '#31B686', '#E56FA8', ROXO[400], '#B0A8C0'],
  restante: '#8B86A0',
  funnel: [ROXO[200], '#B99AD6', '#A47FC7', ROXO[400]],
  paletas: paletasEscuras,
  font,
  leading,
  tracking,
  weight,
  space,
  radius,
  shadow: { ...SOMBRAS_ESCURAS, card: SOMBRAS_ESCURAS.md },
  motion: MOVIMENTO,
};

export const dsTheme: DsTheme = dsLight;

export const styledDs = baseStyled as unknown as ThemedStyledInterface<DsTheme>;

/**
 * Seletor que dobra a própria classe para vencer o tema do DevExpress, que
 * estiliza `.dx-theme-generic-typography h1..h6` e `a` no body inteiro — classe
 * + tag ganha de uma classe sozinha do styled-components. O DevExtreme só põe
 * essa classe no body quando um painel legado carrega, então o conflito aparece
 * depois de abrir um. Enquanto as duas interfaces dividem a mesma página, todo
 * título e todo link do design system precisa dele.
 */
export const acimaDoTemaDx = '&&';

/** Anel de foco único da interface. `recuo` acomoda quem tem borda própria. */
export function anelDeFoco(recuo = '2px') {
  return css`
    outline: 2px solid ${({ theme }) => theme.colors.accent};
    outline-offset: ${recuo};
  `;
}

/**
 * Título de bloco: cartão, seção de página, cabeçalho do viewer. Bold com
 * tracking negativo, como o guia pede para todo título.
 */
export const tituloDeSecao = css`
  ${acimaDoTemaDx} {
    font-size: ${({ theme }) => theme.font.sizeLg};
    line-height: ${({ theme }) => theme.leading.h4};
    letter-spacing: ${({ theme }) => theme.tracking.titulo};
    font-weight: ${({ theme }) => theme.weight.bold};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

/**
 * O eyebrow do guia: miudinha em caixa alta com tracking aberto. Rótulo de
 * filtro, de coluna, de agrupamento.
 */
export const estiloRotulo = css`
  font-size: ${({ theme }) => theme.font.sizeXs};
  font-weight: ${({ theme }) => theme.weight.bold};
  text-transform: uppercase;
  letter-spacing: ${({ theme }) => theme.tracking.eyebrow};
  color: ${({ theme }) => theme.colors.textMuted};
`;

/** O mesmo rótulo quando a tag é um heading e precisa vencer o tema do DevExpress. */
export const rotuloDeGrupo = css`
  ${acimaDoTemaDx} {
    ${estiloRotulo}
  }
`;

/**
 * Aplica transparência a uma cor de status para servir de fundo/borda. Como o
 * resultado é translúcido, a mesma regra funciona no tema claro e no escuro.
 */
export function comAlfa(hex: string, alfa: number): string {
  const valor = hex.replace('#', '');
  const canais = [0, 2, 4].map((inicio) => parseInt(valor.slice(inicio, inicio + 2), 16));

  return `rgba(${canais[0]}, ${canais[1]}, ${canais[2]}, ${alfa})`;
}

function relativeLuminance(hex: string): number {
  const value = hex.replace('#', '');
  const channels = [0, 2, 4].map((start) => {
    const channel = parseInt(value.slice(start, start + 2), 16) / 255;
    return channel <= 0.03928 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
}

export function contrastRatio(foreground: string, background: string): number {
  const first = relativeLuminance(foreground);
  const second = relativeLuminance(background);
  const lighter = Math.max(first, second);
  const darker = Math.min(first, second);
  return (lighter + 0.05) / (darker + 0.05);
}
